// src/components/AdminPanel/HiredCandidates.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  collection, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import LoadingAnimation from "../LoadingAnimation";

const HIRED_STATUS = {
  Active: { bg: "#38a169", color: "#fff" },
  "On Leave": { bg: "#d69e2e", color: "#fff" },
  Completed: { bg: "#3182ce", color: "#fff" },
  Terminated: { bg: "#e53e3e", color: "#fff" },
};

const formatNiceDate = (dateVal) => {
  if (!dateVal) return "—";
  let d;
  if (typeof dateVal.toDate === "function") d = dateVal.toDate();
  else if (typeof dateVal === "string") {
    const p = dateVal.split("-");
    d = p.length === 3 && p[0].length === 2
      ? new Date(`${p[2]}-${p[1]}-${p[0]}`)
      : new Date(dateVal);
  } else if (dateVal instanceof Date) d = dateVal;
  else if (typeof dateVal === "number") d = new Date(dateVal);
  if (!d || isNaN(d.getTime())) return typeof dateVal === "string" ? dateVal : "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const cardGradient = "linear-gradient(135deg, #FF8C00 0%, #E67E00 100%)";
const highlightedGradient = "linear-gradient(135deg, #FF9F2E 0%, #FF8C00 100%)";

const HiredCandidates = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [editModal, setEditModal] = useState({ visible: false, record: null });
  const [confirmDelete, setConfirmDelete] = useState({ visible: false, record: null });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectAllRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "hired"), (snap) => {
      setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => { setSelectedIds(new Set()); }, [activeTab]);

  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const counts = useMemo(() => {
    const c = { Active: 0, "On Leave": 0, Completed: 0, Terminated: 0 };
    records.forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [records]);

  const currentRecords = useMemo(() => {
    const filtered = activeTab === "All" ? records : records.filter((r) => r.status === activeTab);
    return [...filtered].sort((a, b) =>
      (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0)
    );
  }, [records, activeTab]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedIds.size > 0 && selectedIds.size < currentRecords.length;
    }
  }, [selectedIds, currentRecords]);

  const handleToggleDropdown = (e, id) => {
    e.stopPropagation();
    if (openDropdown === id) { setOpenDropdown(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenDropdown(id);
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? new Set(currentRecords.map((r) => r.id)) : new Set());
  };

  const handleSelectRow = (e, id) => {
    const s = new Set(selectedIds);
    e.target.checked ? s.add(id) : s.delete(id);
    setSelectedIds(s);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setOpenDropdown(null);
    try {
      await updateDoc(doc(db, "hired", id), { status: newStatus, updatedAt: serverTimestamp() });
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error("Failed to update status");
      console.error(err);
    }
  };

  const handleDelete = async () => {
    const { record } = confirmDelete;
    if (!record) return;
    setConfirmDelete({ visible: false, record: null });
    try {
      await deleteDoc(doc(db, "hired", record.id));
      toast.success("Record removed");
    } catch (err) {
      toast.error("Failed to remove record");
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    const selected = currentRecords.filter((r) => selectedIds.has(r.id));
    try {
      for (const r of selected) await deleteDoc(doc(db, "hired", r.id));
      toast.success(`${selected.length} record(s) removed`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error("Bulk delete failed");
      console.error(err);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const { record } = editModal;
    try {
      await updateDoc(doc(db, "hired", record.id), {
        position: record.position,
        department: record.department,
        startDate: record.startDate,
        salary: record.salary,
        status: record.status,
        notes: record.notes,
        updatedAt: serverTimestamp(),
      });
      toast.success("Details updated");
      setEditModal({ visible: false, record: null });
    } catch (err) {
      toast.error("Failed to save changes");
      console.error(err);
    }
  };

  const tabs = ["All", "Active", "On Leave", "Completed", "Terminated"];

  if (loading) return <LoadingAnimation text="Loading Hired Employees..." />;

  return (
    <div className="dashboard-page">
      <h1 className="page-heading">
        <i className="fas fa-user-tie" style={{ color: "#FF8C00" }}></i> Hired Employees
      </h1>

      {/* Stats Cards */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "30px", flexWrap: "wrap" }}>
        {[
          { label: "Total Hired", count: records.length, icon: "fas fa-user-tie", bg: highlightedGradient, featured: true },
          { label: "Active", count: counts.Active, icon: "fas fa-check-circle", bg: cardGradient },
          { label: "On Leave", count: counts["On Leave"], icon: "fas fa-coffee", bg: cardGradient },
          { label: "Completed", count: counts.Completed, icon: "fas fa-flag-checkered", bg: cardGradient },
          { label: "Terminated", count: counts.Terminated, icon: "fas fa-times-circle", bg: cardGradient },
        ].map((s, i) => (
          <div key={i} style={{
            background: s.bg, padding: "24px", borderRadius: "16px", color: "white",
            minWidth: s.featured ? "200px" : "160px", flex: s.featured ? undefined : 1,
            boxShadow: s.featured ? "0 8px 20px rgba(255,140,0,0.3)" : "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.15)",
            transform: s.featured ? "translateY(-2px)" : "none",
            transition: "all 0.3s ease",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <i className={s.icon} style={{ fontSize: "28px", opacity: 0.9 }}></i>
              <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "500", opacity: 0.9 }}>{s.label}</h3>
            </div>
            <p style={{ margin: 0, fontSize: "36px", fontWeight: "bold" }}>{s.count}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="category-tabs" style={{ display: "flex", gap: "0", borderBottom: "1px solid #E2E8F0", marginBottom: "20px" }}>
        {tabs.map((tab) => (
          <button key={tab} className={activeTab === tab ? "tab active" : "tab"} onClick={() => setActiveTab(tab)}
            style={{ padding: "12px 24px", background: activeTab === tab ? "#FF8C00" : "transparent", color: activeTab === tab ? "white" : "#4A5568", border: "none", borderRadius: "8px 8px 0 0", cursor: "pointer", fontSize: "14px", fontWeight: "600", transition: "all 0.2s", marginRight: "4px" }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Bulk Toolbar */}
      {selectedIds.size > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", padding: "12px 16px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <span style={{ fontWeight: "600", fontSize: "14px", color: "#4a5568" }}>{selectedIds.size} Selected</span>
          <button onClick={handleBulkDelete} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", background: "#e53e3e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
            <i className="fas fa-trash-alt"></i> Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="candidate-table-container" style={{ marginTop: "20px" }}>
        <div style={{ overflowX: "auto", minHeight: "400px", paddingBottom: currentRecords.length >= 5 ? "180px" : "0px" }}>
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: "40px" }}>
                  <input ref={selectAllRef} type="checkbox" className="oval-checkbox"
                    checked={currentRecords.length > 0 && selectedIds.size === currentRecords.length}
                    onChange={handleSelectAll} />
                </th>
                <th>Name</th>
                <th>Contact</th>
                <th>Position</th>
                <th>Department</th>
                <th>Start Date</th>
                <th>Salary</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Hired On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.length > 0 ? currentRecords.map((record) => (
                <tr key={record.id}>
                  <td>
                    <input type="checkbox" className="oval-checkbox"
                      checked={selectedIds.has(record.id)}
                      onChange={(e) => handleSelectRow(e, record.id)} />
                  </td>
                  <td><strong>{record.name || "—"}</strong></td>
                  <td>
                    <div style={{ fontSize: "13px" }}>{record.email || "—"}</div>
                    <div style={{ fontSize: "12px", color: "#718096" }}>{record.phone || "—"}</div>
                  </td>
                  <td>
                    <span style={{ backgroundColor: "#FF8000", color: "white", padding: "4px 8px", borderRadius: "12px", fontSize: "12px", display: "inline-block" }}>
                      {record.position || "Not Set"}
                    </span>
                  </td>
                  <td>{record.department || "—"}</td>
                  <td>{record.startDate ? formatNiceDate(record.startDate) : "—"}</td>
                  <td>{record.salary || "—"}</td>
                  <td>
                    {record.interviewRating
                      ? <span style={{ fontWeight: "600", color: "#FF8C00" }}>{record.interviewRating}/10</span>
                      : "—"}
                  </td>
                  <td>
                    <p className="status_chip" style={{ backgroundColor: HIRED_STATUS[record.status]?.bg || "#718096", color: "#fff", padding: "4px 12px", borderRadius: "20px", fontSize: "12px", display: "inline-block", margin: 0, fontWeight: 600 }}>
                      {record.status || "Active"}
                    </p>
                  </td>
                  <td>{formatNiceDate(record.createdAt)}</td>
                  <td style={{ textAlign: "center" }}>
                    <button className="action-button" onClick={(e) => handleToggleDropdown(e, record.id)}
                      style={{ background: "transparent", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontSize: "20px", fontWeight: "bold", color: "#4A5568", lineHeight: 1, transition: "background 0.15s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                      &#8942;
                    </button>
                    {openDropdown === record.id && createPortal(
                      <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: `${dropdownPos.top}px`, right: `${dropdownPos.right}px`, background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", zIndex: 999999, minWidth: "180px", overflow: "hidden" }}>
                        <button onClick={() => { setEditModal({ visible: true, record: { ...record } }); setOpenDropdown(null); }}
                          style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#2d3748", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                          <i className="fas fa-edit" style={{ width: "14px" }}></i> Edit Details
                        </button>
                        <div style={{ borderTop: "1px solid #e2e8f0", padding: "4px 0" }}>
                          <div style={{ padding: "4px 16px 2px", fontSize: "10px", fontWeight: "700", color: "#a0aec0", textTransform: "uppercase", letterSpacing: "0.5px" }}>Change Status</div>
                          {Object.keys(HIRED_STATUS).map((status) => (
                            <button key={status} onClick={() => handleStatusUpdate(record.id, status)}
                              style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px", padding: "8px 16px", background: "transparent", border: "none", cursor: "pointer", color: record.status === status ? "#FF8C00" : "#4a5568", fontSize: "13px", fontWeight: record.status === status ? "700" : "500", textAlign: "left" }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "#f7f8fa")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                              {record.status === status && <i className="fas fa-check" style={{ fontSize: "10px" }}></i>}
                              {status}
                            </button>
                          ))}
                        </div>
                        <div style={{ borderTop: "1px solid #e2e8f0" }}>
                          <button onClick={() => { setConfirmDelete({ visible: true, record }); setOpenDropdown(null); }}
                            style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer", color: "#e53e3e", fontSize: "13px", fontWeight: "500", textAlign: "left" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#fff5f5")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                            <i className="fas fa-trash" style={{ width: "14px" }}></i> Remove Record
                          </button>
                        </div>
                      </div>,
                      document.body
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center", padding: "60px 20px" }}>
                    <i className="fas fa-user-tie" style={{ fontSize: "48px", display: "block", marginBottom: "16px", color: "#e2e8f0" }}></i>
                    <p style={{ fontSize: "16px", fontWeight: "600", margin: "0 0 8px", color: "#718096" }}>No Hired Employees Yet</p>
                    <p style={{ fontSize: "13px", margin: 0, color: "#a0aec0" }}>
                      Go to <strong>Interview Management</strong> and move successful job candidates here
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete.visible && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, backdropFilter: "blur(4px)" }}>
          <div style={{ background: "white", padding: "32px", borderRadius: "16px", textAlign: "center", width: "360px", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
            <div style={{ width: "52px", height: "52px", background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <span style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}>!</span>
            </div>
            <h3 style={{ margin: "0 0 12px", color: "#2D3748", fontSize: "18px" }}>Remove Record?</h3>
            <p style={{ margin: "0 0 28px", color: "#718096", fontSize: "14px", lineHeight: "1.5" }}>
              Remove <strong style={{ color: "#2D3748" }}>{confirmDelete.record?.name}</strong> from hired employees? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button onClick={() => setConfirmDelete({ visible: false, record: null })} style={{ flex: 1, padding: "12px 24px", border: "1px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", background: "transparent", color: "#718096", fontWeight: "600" }}>Cancel</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: "12px 24px", border: "none", borderRadius: "10px", cursor: "pointer", fontWeight: "600", color: "white", background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)" }}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Details Modal */}
      {editModal.visible && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, backdropFilter: "blur(4px)" }}
          onClick={() => setEditModal({ visible: false, record: null })}>
          <div style={{ background: "white", padding: "32px", borderRadius: "16px", width: "480px", maxWidth: "95vw", boxShadow: "0 20px 40px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px", fontSize: "20px", color: "#2d3748", fontWeight: "700" }}>Edit Employee Details</h3>
            <p style={{ margin: "0 0 24px", color: "#718096", fontSize: "13px" }}>{editModal.record?.name}</p>
            <form onSubmit={handleSaveEdit}>
              {[
                { label: "Position / Job Title", key: "position", type: "text", placeholder: "e.g., Frontend Developer" },
                { label: "Department", key: "department", type: "text", placeholder: "e.g., Engineering, Design" },
                { label: "Start Date", key: "startDate", type: "date", placeholder: "" },
                { label: "Salary / Stipend", key: "salary", type: "text", placeholder: "e.g., PKR 80,000 / month" },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key} style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", marginBottom: "6px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>{label}</label>
                  <input type={type} placeholder={placeholder} value={editModal.record[key] || ""}
                    onChange={(e) => setEditModal({ ...editModal, record: { ...editModal.record, [key]: e.target.value } })}
                    style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px", boxSizing: "border-box", fontSize: "14px" }} />
                </div>
              ))}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>Status</label>
                <select value={editModal.record.status || "Active"}
                  onChange={(e) => setEditModal({ ...editModal, record: { ...editModal.record, status: e.target.value } })}
                  style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px", fontSize: "14px" }}>
                  {Object.keys(HIRED_STATUS).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontSize: "14px", color: "#4a5568", fontWeight: "600" }}>Notes</label>
                <textarea rows="4" placeholder="Add notes about this employee..."
                  value={editModal.record.notes || ""}
                  onChange={(e) => setEditModal({ ...editModal, record: { ...editModal.record, notes: e.target.value } })}
                  style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e0", borderRadius: "8px", resize: "vertical", boxSizing: "border-box", fontSize: "14px" }}></textarea>
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <button type="button" onClick={() => setEditModal({ visible: false, record: null })} style={{ flex: 1, padding: "12px", background: "transparent", color: "#4a5568", border: "1.5px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", fontWeight: "600", fontSize: "14px" }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: "12px", background: "linear-gradient(135deg, #FF9F2E, #FF8C00)", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontWeight: "700", fontSize: "14px" }}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HiredCandidates;
